
import React, { useState, useRef, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/toast/index';
import { useAuth } from '@/context/AuthContext';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import { useNavigate } from 'react-router-dom';

// Reset password schema
const resetPasswordSchema = z.object({
  email: z.string().email({ message: "Ju lutem vendosni një email të vlefshëm" }),
});

const AuthSection = () => {
  const [activeTab, setActiveTab] = useState<string>("login");
  const [showResetForm, setShowResetForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resetRequestTime, setResetRequestTime] = useState<number | null>(null);
  const { resetPassword } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const isNavigatingRef = useRef(false);
  const isMountedRef = useRef(true);

  // Track component mount state
  useEffect(() => {
    isMountedRef.current = true;
    isNavigatingRef.current = false;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reset password form
  const resetPasswordForm = useForm<z.infer<typeof resetPasswordSchema>>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  // Password reset handler with improved flow
  const handleResetPassword = async (values: z.infer<typeof resetPasswordSchema>) => {
    if (isNavigatingRef.current || !isMountedRef.current) return;
    
    try {
      // Check if we need to wait before requesting again
      const now = Date.now();
      if (resetRequestTime && now - resetRequestTime < 30000) {
        // 30 seconds wait time
        const remainingSeconds = Math.ceil((30000 - (now - resetRequestTime)) / 1000);
        toast({
          title: "Ju lutemi prisni",
          description: `Ju lutemi prisni ${remainingSeconds} sekonda përpara se të dërgoni një kërkesë të re.`,
          variant: "destructive",
        });
        return;
      }
      
      setIsLoading(true);
      
      // Set navigating flag before API call to prevent any other state changes
      isNavigatingRef.current = true;
      
      await resetPassword(values.email);
      
      // Store the time of this request
      setResetRequestTime(Date.now());
      
      // Reset the form state
      resetPasswordForm.reset();
      
      // Prepare email for navigation
      const encodedEmail = encodeURIComponent(values.email);
      
      // Navigate directly to the confirmation page without showing toast here
      navigate(`/auth/password-reset-sent?email=${encodedEmail}`, { replace: true });
      
    } catch (error: any) {
      // Only update UI if component is still mounted and not navigating
      if (isMountedRef.current && !isNavigatingRef.current) {
        console.error('Password reset error:', error);
        toast({
          title: "Gabim",
          description: error.message || "Ka ndodhur një gabim gjatë dërgimit të email-it për rivendosjen e fjalëkalimit.",
          variant: "destructive",
        });
        setIsLoading(false);
        isNavigatingRef.current = false;
      }
    }
  };

  if (showResetForm) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Rivendosni fjalëkalimin</CardTitle>
          <CardDescription>
            Vendosni email-in tuaj dhe do t'ju dërgojmë instruksione për rivendosjen e fjalëkalimit.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...resetPasswordForm}>
            <form onSubmit={resetPasswordForm.handleSubmit(handleResetPassword)} className="space-y-4">
              <FormField
                control={resetPasswordForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="email@example.com" 
                        type="email"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <Button 
                type="submit" 
                className="w-full bg-brand-500 hover:bg-brand-600"
                disabled={isLoading || isNavigatingRef.current}
              >
                {isLoading ? "Duke u dërguar..." : "Dërgo instruksionet"}
              </Button>
              
              <Button 
                type="button" 
                variant="link" 
                className="w-full"
                onClick={() => setShowResetForm(false)}
                disabled={isLoading || isNavigatingRef.current}
              >
                Kthehu tek hyrja
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <Tabs
        defaultValue="login"
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="grid grid-cols-2 w-full mb-8">
          <TabsTrigger value="login">Hyrje</TabsTrigger>
          <TabsTrigger value="register">Regjistrohu</TabsTrigger>
        </TabsList>
        
        <TabsContent value="login" className="mt-0">
          <LoginForm 
            onSwitchToRegister={() => setActiveTab("register")} 
            onForgotPassword={() => setShowResetForm(true)}
          />
        </TabsContent>
        
        <TabsContent value="register" className="mt-0">
          <RegisterForm onSwitchToLogin={() => setActiveTab("login")} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AuthSection;
